import { useCallback } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { useIsLoggedIn, useIsLoggingIn } from '@order/AuthMachine';
import { useCustomer } from '@order/Customer';
import type { Location } from '@order/graphql';
import { useInStoreOrderLastPendingFeedbackQuery } from '@order/graphql';
import { useDeliveryRestaurantQuery } from '@order/graphql';
import { useCart, useIsCustomerSessionReady } from '@order/hooks';
import { useOrders } from '@order/Order';

import { useGetOrderStatusQuery } from '../../GraphQL/OrderStatus.query.generated';

export const useHomeData = () => {
  // ─── Cart & Customer ────────────────────────────────────────────

  const { isCartEmpty, cartOrderType } = useCart();
  const { customer, isFetchingCustomer } = useCustomer();
  const customerAddress = customer?.addresses && customer.addresses[0];

  // ─── Auth ────────────────────────────────────────────────────────

  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();
  const isCustomerSessionReady = useIsCustomerSessionReady();
  const isCustomerReady =
    !isLoggingIn &&
    !isFetchingCustomer &&
    (!isLoggedIn || Boolean(customer?.id));

  // ─── Order History ──────────────────────────────────────────────

  const { orders, isFetchingOrders, hasErrorOrders, fetchOrders } = useOrders();

  // ─── In Store Order Last Pending Feedback ───────────────────────

  const [response, fetchFeedback] = useInStoreOrderLastPendingFeedbackQuery({
    requestPolicy: 'cache-and-network',
    pause: true,
  });
  const orderPendingFeedbackId =
    response?.data?.inStoreOrderLastPendingFeedback?.id;

  // ─── Order Status ───────────────────────────────────────────────

  const [statusResponse, fetchStatus] = useGetOrderStatusQuery({
    requestPolicy: 'network-only',
    pause: true,
  });
  const hasErrorStatus = Boolean(statusResponse.error);
  const isLoadingStatus = statusResponse.fetching || statusResponse.stale;
  const status = !isLoadingStatus ? statusResponse.data : undefined;

  // ─── Delivery Restaurant ────────────────────────────────────────

  const customerDeliveryAddressId = customerAddress?.id ?? '';
  const [restaurantResponse] = useDeliveryRestaurantQuery({
    variables: { input: { addressId: customerDeliveryAddressId } },
    pause: !customerDeliveryAddressId,
  });

  const isLoadingDeliveryRestaurant =
    restaurantResponse.fetching || restaurantResponse.stale;
  const locationByAddressResponse = restaurantResponse.data
    ?.locationByAddress as Location;
  const deliveryRestaurant = locationByAddressResponse?.restaurant;
  const deliveryVendor = locationByAddressResponse?.vendor;

  // ─── Refetch on Focus ───────────────────────────────────────────

  useRefetchOnFocus(fetchOrders, isCustomerSessionReady);
  useRefetchOnFocus(fetchStatus, isCustomerSessionReady);
  useRefetchOnFocus(fetchFeedback, isCustomerSessionReady);

  return {
    orders,
    status,
    orderPendingFeedbackId,
    deliveryRestaurant,
    deliveryVendor,
    cartOrderType,
    isCartEmpty,
    isLoggedIn,
    isCustomerReady,
    isLoading: !isCustomerReady || isLoggingIn || isLoadingDeliveryRestaurant,
    isLoadingOrders: isFetchingOrders,
    isLoadingStatus,
    hasErrors: hasErrorOrders || hasErrorStatus,
  };
};

const useRefetchOnFocus = (fn: () => void, shouldRefetch: boolean) => {
  useFocusEffect(
    useCallback(() => {
      if (shouldRefetch) fn();
    }, [shouldRefetch, fn]),
  );
};
